import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Loader2, PartyPopper, AlertTriangle, RefreshCw, Bot } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

export const InsightWidget = () => {
    const [insight, setInsight] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/admin/insights?limit=1");
            setInsight(data[0] || null);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    const generate = async () => {
        try {
            setGenerating(true);
            const { data } = await api.post("/admin/insights/generate");
            setInsight(data);
            toast.success("Fresh insight generated.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setGenerating(false);
        }
    };

    useEffect(() => { load(); }, [load]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            data-testid="insight-widget"
            className="card-soft p-5 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-sky-500/5"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                        <Bot size={16} />
                        <span className="overline">AI Executive Insights</span>
                    </div>
                    <h2 className="mt-1 font-heading font-semibold text-lg text-foreground">
                        {loading
                            ? "Loading latest insight…"
                            : insight?.headline || "No insight yet — generate your first weekly narrative."}
                    </h2>
                    {insight && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            Generated {new Date(insight.generated_at).toLocaleString()} · by {insight.generated_by}
                        </div>
                    )}
                </div>
                <button
                    onClick={generate}
                    disabled={generating}
                    className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="insight-generate"
                >
                    {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate fresh
                </button>
            </div>

            {loading ? (
                <div className="py-6 flex justify-center"><Loader2 size={16} className="animate-spin text-emerald-500" /></div>
            ) : insight ? (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                        <ul className="space-y-2">
                            {insight.bullets?.map((b, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="space-y-3">
                        {insight.celebrate && (
                            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3">
                                <div className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">
                                    <PartyPopper size={11} /> Celebrate
                                </div>
                                <p className="mt-1 text-xs text-foreground">{insight.celebrate}</p>
                            </div>
                        )}
                        {insight.risks?.length > 0 && (
                            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3">
                                <div className="flex items-center gap-1.5 text-[10px] font-semibold text-amber-600 uppercase tracking-wider">
                                    <AlertTriangle size={11} /> Watch
                                </div>
                                <ul className="mt-1 space-y-1">
                                    {insight.risks.map((r, i) => (
                                        <li key={i} className="text-xs text-foreground">{r}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="mt-4 text-sm text-muted-foreground">
                    Click &quot;Generate fresh&quot; and Simonara AI will synthesize your week-over-week platform stats into a punchy executive brief.
                </div>
            )}
        </motion.div>
    );
};

export default InsightWidget;
